import { INGLIS_BLUE } from '../../constants/colors'
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useMapContext } from '../../context/MapContext'
import { usePwaInstall } from '../../hooks/usePwaInstall'
import { useAccessibilityPrefs } from '../../hooks/useAccessibilityPrefs'
import { downloadSiteDirectoryPdf } from '../../utils/pdfExport'
import { shouldUsePdfFallback } from '../../utils/printEnvironment'
import { downloadDesktopShortcut, detectShortcutPlatform } from '../../utils/desktopShortcut'
import InstallGuide from '../InstallGuide/InstallGuide'

const PrintIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
    <path d="M6 9V3h12v6M6 18H4v-7h16v7h-2M8 14h8v7H8z"/>
  </svg>
)

const InstallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
    <path d="M12 3v12m0 0-4-4m4 4 4-4M5 21h14"/>
  </svg>
)

const ContrastIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
    <circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none"/>
  </svg>
)

const btn: CSSProperties = {
  alignItems: 'center', gap: 6,
  padding: '0 12px', height: 36, minWidth: 44, flexShrink: 0,
  border: '1px solid rgba(255,255,255,0.38)',
  borderRadius: 6, background: 'rgba(255,255,255,0.08)',
  color: '#fff', fontWeight: 600, fontSize: '0.82rem',
  cursor: 'pointer', whiteSpace: 'nowrap',
}

// Vertical room the instructions popover needs below its anchor before it flips to sitting above
// it — a little over its rendered height, so a near-the-bottom anchor (the mobile FAB) flips.
// Ties the Install triggers' aria-controls to the steps panel they open.
const MANUAL_STEPS_ID = 'install-manual-steps'

const POPOVER_MIN_SPACE = 200

const iconOnlyBtn: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 36, height: 36, flexShrink: 0,
  border: '1px solid rgba(255,255,255,0.38)',
  borderRadius: 6, background: 'rgba(255,255,255,0.08)',
  color: '#fff', fontWeight: 700, fontSize: '0.78rem',
  cursor: 'pointer',
}

export default function TopBar({ onBackToLanding }: { onBackToLanding?: () => void }) {
  const { viewMode, setViewMode, setSelectedSite } = useMapContext()
  const { canInstall, promptInstall, manualInstructions } = usePwaInstall()
  const { highContrast, largeText, toggleHighContrast, toggleLargeText } = useAccessibilityPrefs()
  const [redirecting, setRedirecting] = useState(false)
  // Even after the mobile decrowding pass (icon-only Start Over/Map/List below sm), the header
  // can still overflow on the smallest real phones (measured: ~320-390px CSS width) — and
  // overflow-x-auto gives no visual cue that swiping reveals more. This tracks whether there's
  // currently unscrolled content to the right, so a fade affordance (rendered below) can hint at
  // it rather than leaving A+/contrast/Print silently past the edge with zero indication.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [hasMoreToScroll, setHasMoreToScroll] = useState(false)
  const [showManualSteps, setShowManualSteps] = useState(false)
  // The clicked button's on-screen rect, captured at click time — the popover is positioned
  // `fixed` off this (see the render below for why it can't be positioned relative to the button
  // in the DOM). Cleared when the popover closes.
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
  const [shortcutMsg, setShortcutMsg] = useState('')
  // The Install button that opened the steps, so focus can go back to it on close. The panel is
  // rendered at the end of the component (it has to escape the header's overflow clipping — see
  // its comment below), which puts it nowhere near its trigger in the DOM: without moving focus
  // deliberately, a keyboard user pressing Install would Tab straight past the content they just
  // asked for, and a screen-reader user following the tab order would never meet it at all.
  const installTriggerRef = useRef<HTMLButtonElement | null>(null)
  const manualStepsRef = useRef<HTMLDivElement>(null)
  const showInstall = canInstall || !!manualInstructions

  function closeManualSteps() {
    setShowManualSteps(false)
    setAnchorRect(null)
    setShortcutMsg('')
    // Escape, a second press of Install, or the Got it button all land here — focus goes back to
    // the control the user was on, never to the top of the document.
    installTriggerRef.current?.focus()
    installTriggerRef.current = null
  }

  function handleDownloadShortcut() {
    try {
      downloadDesktopShortcut()
      const platform = detectShortcutPlatform()
      setShortcutMsg(
        platform === 'linux'
          ? 'Saved to Downloads. Move it where you want it, then mark it executable to open it.'
          : platform === 'macos'
            ? 'Saved to Downloads — drag it onto your Dock to keep it there.'
            : 'Saved to Downloads — drag it to your Desktop or taskbar.'
      )
    } catch {
      setShortcutMsg('Could not create the shortcut. Please try again.')
    }
  }

  function handleInstallClick(e: ReactMouseEvent<HTMLButtonElement>) {
    if (canInstall) {
      void promptInstall()
      return
    }
    if (showManualSteps) {
      closeManualSteps()
    } else {
      installTriggerRef.current = e.currentTarget
      setAnchorRect(e.currentTarget.getBoundingClientRect())
      setShowManualSteps(true)
    }
  }

  // Depends on anchorRect, not just showManualSteps: the panel only mounts once the rect is
  // measured, so focusing on the showManualSteps flip alone would run a render too early.
  useEffect(() => {
    if (showManualSteps && anchorRect) manualStepsRef.current?.focus()
  }, [showManualSteps, anchorRect])

  // A resize/scroll invalidates the captured anchor rect, and Escape should dismiss any popover.
  // Closing is the honest response to all three — a stale rect would park the popover away from
  // the button it belongs to.
  useEffect(() => {
    if (!showManualSteps) return
    function onKeyDown(ev: KeyboardEvent) {
      if (ev.key === 'Escape') closeManualSteps()
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', closeManualSteps)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', closeManualSteps)
    }
  }, [showManualSteps])

  function handlePrint() {
    if (shouldUsePdfFallback()) {
      void downloadSiteDirectoryPdf()
    } else {
      window.print()
    }
  }

  // On a mobile PWA, opening inglis.org hands off to an external browser and our page
  // flashes blank (black) during the launch. Cover that gap with a spinner overlay, and
  // clear it once the user comes back (visibility restored) with a timeout as a fallback.
  useEffect(() => {
    if (!redirecting) return
    function clear() {
      if (document.visibilityState === 'visible') setRedirecting(false)
    }
    const fallback = window.setTimeout(() => setRedirecting(false), 8000)
    document.addEventListener('visibilitychange', clear)
    window.addEventListener('pageshow', clear)
    return () => {
      window.clearTimeout(fallback)
      document.removeEventListener('visibilitychange', clear)
      window.removeEventListener('pageshow', clear)
    }
  }, [redirecting])

  // Re-measures on mount, on scroll, on window resize, on any size change of the scroll
  // container or the controls inside it, and whenever a pref/button that changes the row's width
  // flips. A window `resize` listener alone missed two real cases, both of which grow scrollWidth
  // without touching the container's own clientWidth and without firing scroll or resize:
  //   - the Install button appearing after mount, once usePwaInstall resolves eligibility;
  //   - the A+ / large-text and high-contrast toggles, which restyle every label at once.
  // The second one matters most: it left the fade hidden for precisely the users who had just
  // asked for bigger text and were therefore the most likely to have controls pushed off-screen.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    function updateScrollHint() {
      if (!el) return
      // 1px slack for sub-pixel rounding at some zoom levels/devicePixelRatios.
      setHasMoreToScroll(el.scrollWidth - el.scrollLeft - el.clientWidth > 1)
    }
    updateScrollHint()
    el.addEventListener('scroll', updateScrollHint, { passive: true })
    window.addEventListener('resize', updateScrollHint)
    // Guarded: jsdom has no ResizeObserver. The container's own box rarely changes on its own, so
    // the children are observed too — they're the things that actually widen when text grows.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollHint)
    if (observer) {
      observer.observe(el)
      for (const child of Array.from(el.children)) observer.observe(child)
    }
    return () => {
      el.removeEventListener('scroll', updateScrollHint)
      window.removeEventListener('resize', updateScrollHint)
      observer?.disconnect()
    }
  }, [showInstall, largeText, highContrast])

  function handleLogoClick(e: ReactMouseEvent<HTMLAnchorElement>) {
    // Only the mobile hand-off shows the blank flash; desktop opens a new tab and stays put,
    // so let the anchor behave normally there.
    if (!window.matchMedia('(max-width: 639px)').matches) return
    // Take over the navigation so the overlay is guaranteed to paint BEFORE we leave the
    // page — otherwise React never gets a frame and the spinner is never seen. Navigate in
    // the same tab after a couple of frames so the blue spinner covers the launch flash.
    e.preventDefault()
    setRedirecting(true)
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        window.location.href = 'https://inglis.org'
      })
    )
  }

  return (
    <>
    <header
      // shrink-0 has to stay on the <header> itself, not the inner scroll container below: the
      // header is the flex item of App's `flex flex-col` shell, and the inner div is a plain block
      // child where the class is inert. Without it the header absorbs the entire shortfall in a
      // viewport shorter than header + CombinedBar (~92px), since CombinedBar carries its own
      // shrink-0 and <main> has a zero flex-basis — the bar collapses and clips the logo.
      className="shrink-0"
      style={{
        background: INGLIS_BLUE,
        boxShadow: '0 2px 8px rgba(0,0,0,.14)',
        position: 'relative',
        zIndex: 40,
        // Installed as a standalone PWA, this bar sits under the status bar/notch with no browser
        // chrome to absorb it — pad by the safe-area inset so the logo/buttons clear the notch
        // instead of the fixed 52px height clipping into it.
        height: 'calc(52px + env(safe-area-inset-top, 0px))',
        boxSizing: 'border-box',
      }}
    >
      <div
        ref={scrollRef}
        className="text-white flex items-center gap-2 sm:gap-4 px-2 sm:px-4 overflow-x-auto"
        style={{
          height: '100%',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          boxSizing: 'border-box',
        }}
      >
      <h1 className="sr-only">Inglis Enabled Home Showcase</h1>
      <a href="https://inglis.org" target="_blank" rel="noopener noreferrer" aria-label="Inglis" className="shrink-0" onClick={handleLogoClick}>
        <img src="/inglis-logo-white.png" alt="Inglis — Ability & Independence. Redefined." width={107} height={36} style={{ height: 36, width: 'auto' }} />
      </a>
      <span className="hidden sm:inline" style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap', borderLeft: '1px solid rgba(255,255,255,0.25)', paddingLeft: 14 }}>
        Enabled Home Showcase
      </span>

      <div className="flex-1" />

      {/* Back to landing */}
      <button
        type="button"
        onClick={() => onBackToLanding?.()}
        aria-label="Start over — back to landing page"
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '0 10px', height: 28,
          border: '1px solid rgba(255,255,255,0.28)',
          borderRadius: 20,
          background: 'rgba(255,255,255,0.08)',
          color: 'rgba(255,255,255,0.8)',
          fontSize: '0.78rem', fontWeight: 600,
          cursor: 'pointer', whiteSpace: 'nowrap',
          marginRight: 4, flexShrink: 0,
        }}
      >
        <span aria-hidden="true">←</span> <span className="hidden sm:inline">Start Over</span>
      </button>

      <div className="flex items-center shrink-0" style={{ gap: 6 }}>
        <div style={{ display: 'flex', border: '1px solid rgba(255,255,255,0.38)', borderRadius: 6, overflow: 'hidden', height: 36, flexShrink: 0 }}>
          <button
            type="button"
            className="topbar-toggle-btn"
            onClick={() => { setViewMode('map'); setSelectedSite(null) }}
            aria-pressed={viewMode === 'map'}
            aria-label="Map"
            style={{
              padding: '0 12px', height: '100%',
              background: viewMode === 'map' ? 'rgba(255,255,255,0.92)' : 'transparent',
              color: viewMode === 'map' ? INGLIS_BLUE : 'rgba(255,255,255,0.85)',
              fontWeight: 700, fontSize: '0.82rem', border: 'none', cursor: 'pointer',
            }}
          >
            <span aria-hidden="true">⊞</span> <span className="hidden sm:inline">Map</span>
          </button>
          <button
            type="button"
            className="topbar-toggle-btn"
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            aria-label="List"
            style={{
              padding: '0 12px', height: '100%',
              background: viewMode === 'list' ? 'rgba(255,255,255,0.92)' : 'transparent',
              color: viewMode === 'list' ? INGLIS_BLUE : 'rgba(255,255,255,0.85)',
              fontWeight: 700, fontSize: '0.82rem',
              border: 'none', borderLeft: '1px solid rgba(255,255,255,0.38)', cursor: 'pointer',
            }}
          >
            <span aria-hidden="true">☰</span> <span className="hidden sm:inline">List</span>
          </button>
        </div>

        <button
          type="button"
          onClick={toggleLargeText}
          aria-pressed={largeText}
          // Name matches the visible "A+" label's meaning rather than describing the action:
          // WCAG 2.5.3 wants the accessible name to relate to what is on screen, and aria-pressed
          // already carries the on/off state that the old "Turn on/off …" phrasing duplicated.
          aria-label="Large text"
          title="Large text"
          style={{ ...iconOnlyBtn, background: largeText ? '#fff' : iconOnlyBtn.background, color: largeText ? INGLIS_BLUE : '#fff' }}
        >
          A+
        </button>

        <button
          type="button"
          onClick={toggleHighContrast}
          aria-pressed={highContrast}
          aria-label={highContrast ? 'Turn off high contrast' : 'Turn on high contrast'}
          title="High contrast"
          style={{ ...iconOnlyBtn, background: highContrast ? '#fff' : iconOnlyBtn.background, color: highContrast ? INGLIS_BLUE : '#fff' }}
        >
          <ContrastIcon />
        </button>

        {/* Was `hidden sm:flex` — hid this entirely below 640px, which is effectively every
            phone. Android Chrome (and others) support printing/"Save as PDF" from a normal print
            dialog same as desktop, so there was no real reason to hide the whole control on
            mobile; only the text label collapses now, matching the icon-only pattern used
            elsewhere in this bar (e.g. the subtitle span further up).

            window.print() gives a real print preview, which is worth keeping wherever it
            actually works — shouldUsePdfFallback() detects Windows specifically (its
            "Microsoft Print to PDF" driver rotates the output 90°, regardless of browser — see
            printEnvironment.ts) and falls back to a directly-generated PDF download only there,
            instead of losing the preview for everyone, including macOS/Linux Firefox users
            (Firefox has its own separate, lesser print-dialog quirks — Color toggle disabled,
            an inconsistent Pages section — left as-is rather than losing their preview too). */}
        <button
          type="button"
          onClick={handlePrint}
          className="flex"
          style={btn}
          aria-label="Print this map"
        >
          <PrintIcon />
          <span className="hidden sm:inline">Print</span>
        </button>

        {/* Desktop: install lives in the header. On mobile it moves to a floating
            button at the bottom-right of the page (rendered below). Shown on every
            browser/platform that isn't already running standalone — canInstall drives a real
            native prompt (Chrome/Edge/Samsung Internet); everywhere else (Safari, Firefox, …)
            this opens manualInstructions instead (popover below). */}
        {/* The instructions popover is NOT rendered here as an absolutely-positioned child. This
            header is `overflow-x-auto` and 52px tall, which clips any descendant that extends
            below it — the popover was in the DOM and "visible" per CSS, but painted nowhere, so
            clicking Install looked like it did nothing at all. It's rendered as a
            `position: fixed` sibling outside the header instead (see below). */}
        {showInstall && (
          <button
            type="button"
            onClick={handleInstallClick}
            className="hidden sm:flex"
            style={btn}
            aria-label="Install this app"
            aria-expanded={canInstall ? undefined : showManualSteps}
          aria-controls={!canInstall && showManualSteps ? MANUAL_STEPS_ID : undefined}
          >
            <InstallIcon />
            <span>Install</span>
          </button>
        )}
      </div>

      <a
        href="https://cognitioanalytics.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="hidden md:inline cognitio-credit-link"
        style={{
          borderLeft: '1px solid rgba(255,255,255,0.18)',
          paddingLeft: 14,
          fontSize: '0.78rem',
          color: 'rgba(255,255,255,0.68)',
          whiteSpace: 'nowrap',
          letterSpacing: '0.01em',
          textDecoration: 'none',
        }}>
        Powered by Cognitio Analytics
      </a>
      </div>

      {/* Fade hint that more header content is reachable by scrolling right — purely visual
          (aria-hidden, zero effect on the tab order), sitting outside the scrolling div above so
          it stays pinned to the visible edge instead of scrolling away with the content it's
          pointing at. Only rendered while there's genuinely more to reveal (see the effect
          above), so it never appears once every control is already in view. */}
      {hasMoreToScroll && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 28,
            background: `linear-gradient(to right, transparent, ${INGLIS_BLUE})`,
            pointerEvents: 'none',
          }}
        />
      )}
    </header>

      {/* Mobile-only floating install button (bottom-right of the page). Same canInstall/
          manualInstructions split as the header button above — native prompt where the browser
          supports it, "how to install" popover everywhere else. */}
      {showInstall && (
        <button
          type="button"
          onClick={handleInstallClick}
          // display/alignment live in classes, NOT in the inline style below: an inline
          // `display: flex` outranks every class, which silently defeated both `sm:hidden` (two
          // Install buttons rendered side by side on desktop — header and floating) and
          // `print:hidden` (the floating button printed onto the map).
          className="sm:hidden print:hidden flex items-center justify-center"
          aria-label="Install this app"
          aria-expanded={canInstall ? undefined : showManualSteps}
          aria-controls={!canInstall && showManualSteps ? MANUAL_STEPS_ID : undefined}
          style={{
            position: 'fixed',
            right: 'calc(16px + env(safe-area-inset-right, 0px))',
            bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            zIndex: 50,
            gap: 6,
            height: 48,
            padding: '0 18px',
            border: 'none',
            borderRadius: 999,
            background: INGLIS_BLUE,
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.9rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 14px rgba(0,0,0,0.28)',
          }}
        >
          <InstallIcon />
          <span>Install</span>
        </button>
      )}

      {/* Browsers with no install API but a real install *gesture* (Safari, Android) get a guided
          overlay pointing at the actual control, rather than a paragraph of prose to decode. */}
      {!canInstall && showManualSteps && manualInstructions?.guide && (
        <InstallGuide
          title={manualInstructions.title}
          guide={manualInstructions.guide}
          onClose={closeManualSteps}
          onDownloadShortcut={manualInstructions.canDownloadShortcut ? handleDownloadShortcut : undefined}
          shortcutMsg={shortcutMsg}
          shortcutLabel={detectShortcutPlatform() === 'macos' ? 'Add a shortcut to my Dock' : 'Add shortcut to my computer'}
        />
      )}

      {/* Shared "how to install" popover for both Install buttons above, rendered here — outside
          the header — and positioned `fixed` against the clicked button's measured rect. Both the
          header (overflow-x-auto, 52px tall) and every wrapper between it and the root are
          overflow-clipped, so an in-flow/absolute popover cannot escape them; `fixed` positioned
          off a measured anchor is what actually paints on screen. Flips above the button when
          there isn't room below (the mobile FAB sits at the bottom of the viewport). */}
      {!canInstall && showManualSteps && manualInstructions && !manualInstructions.guide && anchorRect && (
        <div
          // A disclosure, not a dialog. It was previously announced as role="dialog" while nothing
          // moved focus into it and nothing trapped focus inside it — a dialog a screen-reader user
          // could be told about but never enter. It is a short hint panel, not a modal task, so the
          // honest semantics are a labelled group that the trigger owns via aria-expanded +
          // aria-controls, with focus moved here on open and returned on close.
          ref={manualStepsRef}
          id={MANUAL_STEPS_ID}
          role="group"
          tabIndex={-1}
          aria-label={manualInstructions.title}
          className="print:hidden"
          style={{
            position: 'fixed',
            zIndex: 60,
            ...(anchorRect.bottom + POPOVER_MIN_SPACE <= window.innerHeight
              ? { top: anchorRect.bottom + 8 }
              : { bottom: window.innerHeight - anchorRect.top + 8 }),
            right: Math.max(8, window.innerWidth - anchorRect.right),
            width: 260,
            maxWidth: 'calc(100vw - 16px)',
            padding: '14px 16px',
            borderRadius: 10,
            background: '#fff',
            color: '#1a1a1a',
            boxShadow: '0 6px 20px rgba(0,0,0,0.28)',
            fontSize: '0.85rem',
            lineHeight: 1.45,
          }}
        >
          <p style={{ margin: '0 0 8px', fontWeight: 700 }}>{manualInstructions.title}</p>
          <p style={{ margin: 0 }}>{manualInstructions.steps}</p>

          {/* The one thing a page can actually *do* on a browser that exposes no install API:
              hand over a real shortcut file rather than telling the user to press ⌘D. */}
          {manualInstructions.canDownloadShortcut && (
            <>
              <button
                type="button"
                onClick={handleDownloadShortcut}
                style={{
                  marginTop: 12, width: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  height: 34, padding: '0 12px',
                  border: 'none', borderRadius: 6,
                  background: INGLIS_BLUE, color: '#fff',
                  fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                }}
              >
                <InstallIcon />
                <span>Add shortcut to my computer</span>
              </button>
              {/* role="status" so the confirmation is announced — the download itself is silent
                  to assistive tech, and the file lands in Downloads with no visible feedback. */}
              <p role="status" style={{ margin: '8px 0 0', minHeight: '1.2em', color: '#3a6b35' }}>
                {shortcutMsg}
              </p>
            </>
          )}

          <button
            type="button"
            onClick={closeManualSteps}
            style={{
              marginTop: 10, border: 'none', background: 'transparent',
              color: INGLIS_BLUE, fontWeight: 700, fontSize: '0.82rem',
              cursor: 'pointer', padding: 0,
            }}
          >
            Got it
          </button>
        </div>
      )}

      {/* Spinner shown while the mobile PWA hands off to inglis.org, covering the blank flash. */}
      {redirecting && (
        <div className="redirect-overlay" role="status" aria-live="polite">
          <div className="redirect-overlay__spinner" aria-hidden="true" />
          <span className="redirect-overlay__label">Opening inglis.org…</span>
        </div>
      )}
    </>
  )
}
